import React, { useState, useCallback } from 'react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Share2,
  Facebook,
  Linkedin,
  Link2,
  Check,
  Download,
  Copy,
  Instagram,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const BASE_URL = 'https://propertytaxhero.com';

interface ShareMenuProps {
  /** Page path, e.g. "/" or "/colorado" */
  path?: string;
  /** Override share title */
  title?: string;
  /** Override share description */
  description?: string;
  /** Override share image (absolute URL) */
  imageUrl?: string;
  /** Show Instagram fallback options */
  showInstagram?: boolean;
  /** Additional className for the trigger button */
  className?: string;
  /** Button variant */
  variant?: 'icon' | 'button';
}

const ShareMenu: React.FC<ShareMenuProps> = ({
  path,
  title = 'PropertyTaxHero: Free Help to Lower Your Property Tax Bill',
  description = 'Check for overassessment, find your deadline, and prepare a stronger property tax appeal with PropertyTaxHero.',
  imageUrl,
  showInstagram = true,
  className,
  variant = 'icon',
}) => {
  const [copied, setCopied] = useState(false);
  const [captionCopied, setCaptionCopied] = useState(false);
  const [open, setOpen] = useState(false);

  const pageUrl = path
    ? `${BASE_URL}${path}`
    : typeof window !== 'undefined'
      ? window.location.href
      : BASE_URL;

  const encodedUrl = encodeURIComponent(pageUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedDescription = encodeURIComponent(description);

  const handleCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(
        typeof window !== 'undefined' ? window.location.href : pageUrl,
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback
      const ta = document.createElement('textarea');
      ta.value = typeof window !== 'undefined' ? window.location.href : pageUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [pageUrl]);

  const handleCopyCaption = useCallback(async () => {
    const caption = `${title}\n\n${description}\n\n${typeof window !== 'undefined' ? window.location.href : pageUrl}`;
    try {
      await navigator.clipboard.writeText(caption);
      setCaptionCopied(true);
      setTimeout(() => setCaptionCopied(false), 2000);
    } catch {
      // silent fail
    }
  }, [title, description, pageUrl]);

  const handleNativeShare = useCallback(async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title,
          text: description,
          url: typeof window !== 'undefined' ? window.location.href : pageUrl,
        });
        setOpen(false);
      } catch {
        // User cancelled
      }
    }
  }, [title, description, pageUrl]);

  const handleDownloadImage = useCallback(() => {
    const imgSrc = imageUrl || `${BASE_URL}/social-share-square.png`;
    const a = document.createElement('a');
    a.href = imgSrc;
    a.download = 'PropertyTaxHero-Share.png';
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [imageUrl]);

  const shareActions = [
    {
      label: 'Facebook',
      icon: Facebook,
      onClick: () =>
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          '_blank',
          'noopener,noreferrer,width=600,height=400',
        ),
      color: 'text-[#1877F2]',
    },
    {
      label: 'LinkedIn',
      icon: Linkedin,
      onClick: () =>
        window.open(
          `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
          '_blank',
          'noopener,noreferrer,width=600,height=400',
        ),
      color: 'text-[#0A66C2]',
    },
    {
      label: copied ? 'Copied!' : 'Copy Link',
      icon: copied ? Check : Link2,
      onClick: handleCopyLink,
      color: copied ? 'text-green-600' : 'text-muted-foreground',
    },
  ];

  // Check for native share support
  const supportsNativeShare =
    typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {variant === 'icon' ? (
          <button
            className={cn(
              'inline-flex items-center justify-center rounded-full p-2 text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
              className,
            )}
            aria-label="Share this page"
            title="Share this page"
          >
            <Share2 className="h-5 w-5" />
          </button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className={cn('gap-2', className)}
          >
            <Share2 className="h-4 w-4" />
            Share
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-3"
        align="end"
        sideOffset={8}
      >
        <div className="space-y-1">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Share this page
          </p>

          {/* Main share actions */}
          {shareActions.map((action) => (
            <button
              key={action.label}
              onClick={action.onClick}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-muted transition-colors text-foreground"
            >
              <action.icon className={cn('h-4 w-4 flex-shrink-0', action.color)} />
              {action.label}
            </button>
          ))}

          {/* Native share */}
          {supportsNativeShare && (
            <button
              onClick={handleNativeShare}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-muted transition-colors text-foreground"
            >
              <Share2 className="h-4 w-4 flex-shrink-0 text-muted-foreground" />
              More options…
            </button>
          )}

          {/* Instagram fallback */}
          {showInstagram && (
            <>
              <div className="border-t border-border my-2" />
              <p className="text-xs text-muted-foreground px-3 mb-1">
                For Instagram & other platforms:
              </p>
              <button
                onClick={handleDownloadImage}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-muted transition-colors text-foreground"
              >
                <Download className="h-4 w-4 flex-shrink-0 text-pink-500" />
                Download Social Image
              </button>
              <button
                onClick={handleCopyCaption}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium hover:bg-muted transition-colors text-foreground"
              >
                <Copy
                  className={cn(
                    'h-4 w-4 flex-shrink-0',
                    captionCopied ? 'text-green-600' : 'text-pink-500',
                  )}
                />
                {captionCopied ? 'Caption Copied!' : 'Copy Caption & Link'}
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ShareMenu;
